import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bell, AlertTriangle, DollarSign, Loader2, Play, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

const typeMeta = {
  absence_threshold: { icon: AlertTriangle, color: "bg-orange-500", labelKey: "alert_absence" },
  overdue_payment: { icon: DollarSign, color: "bg-red-600", labelKey: "alert_overdue" },
};

function timeAgo(iso, t) {
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 1) return t("just_now");
    if (diff < 60) return `${diff} min`;
    if (diff < 1440) return `${Math.floor(diff / 60)} h`;
    return `${Math.floor(diff / 1440)} d`;
  } catch { return ""; }
}

export default function Alerts() {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState([]);
  const [running, setRunning] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = (showArchivedOnly = includeArchived) =>
    api.get("/alerts", { params: showArchivedOnly ? { archived_only: true } : {} })
      .then((r) => setAlerts(r.data))
      .catch(() => {});
  useEffect(() => { load(); }, [includeArchived]);

  const runAlerts = async () => {
    try {
      setRunning(true);
      const r = await api.post("/admin/run-alerts");
      toast.success(`${t("alerts_done")}: ${r.data.absence_alerts_sent} ${t("alert_absence").toLowerCase()} · ${r.data.overdue_alerts_sent} ${t("alert_overdue").toLowerCase()}`);
      load();
    } catch { toast.error(t("error_generic")); }
    finally { setRunning(false); }
  };

  const toggleArchive = async (a) => {
    try {
      const path = a.archived ? `unarchive` : `archive`;
      await api.post(`/alerts/${a.id}/${path}`);
      toast.success(t(a.archived ? "alert_restored" : "alert_archived"));
      load();
    } catch { toast.error(t("error_generic")); }
  };

  return (
    <div>
      <PageHeader
        title={t("alerts")}
        subtitle={t("alerts_desc")}
        testId="alerts-header"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant={includeArchived ? "default" : "outline"}
              size="sm"
              onClick={() => setIncludeArchived(!includeArchived)}
              data-testid="alerts-toggle-archived"
            >
              <Archive className="h-4 w-4" />
              {includeArchived ? t("alerts_show_active") : t("alerts_show_all")}
            </Button>
            <Button data-testid="run-alerts-button" onClick={runAlerts} disabled={running}>
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {t("run_alerts")}
            </Button>
          </div>
        }
      />

      {alerts.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <Bell className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">{t("no_alerts")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => {
            const meta = typeMeta[a.type] || { icon: Bell, color: "bg-slate-500", labelKey: "alerts" };
            const Icon = meta.icon;
            const p = a.payload || {};
            return (
              <Card key={a.id} className={`p-5 ${a.archived ? "opacity-60 bg-slate-50" : ""}`} data-testid={`alert-${a.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${meta.color}`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="uppercase text-[10px] tracking-wider">{t(meta.labelKey)}</Badge>
                      {a.archived && (
                        <Badge variant="secondary" className="uppercase text-[10px] tracking-wider bg-slate-200 text-slate-600">
                          {t("archived")}
                        </Badge>
                      )}
                      <span className="text-xs text-slate-400">{timeAgo(a.created_at, t)}</span>
                    </div>
                    <h3 className="font-display text-lg font-semibold text-slate-900 mt-2">{p.student_name}</h3>
                    {a.type === "absence_threshold" && (
                      <p className="text-sm text-slate-600 mt-1">
                        {t("alert_absence_msg")
                          .replace("{missed}", p.missed)
                          .replace("{allowed}", p.allowed)
                          .replace("{program}", p.program_name || "")}
                      </p>
                    )}
                    {a.type === "overdue_payment" && (
                      <p className="text-sm text-slate-600 mt-1">
                        {t("alert_overdue_msg")
                          .replace("{days}", p.days_overdue)
                          .replace("{amount}", `$${Number(p.amount).toFixed(2)}`)
                          .replace("{program}", p.program_name || "")}
                      </p>
                    )}
                    <div className="mt-2 text-[11px] text-slate-400">
                      {t("notified")}: {(a.recipients || []).join(" · ") || "—"}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleArchive(a)}
                    className="shrink-0 text-slate-500 hover:text-slate-900"
                    title={a.archived ? t("alert_restore") : t("alert_archive")}
                    data-testid={`alert-toggle-${a.id}`}
                  >
                    {a.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-slate-400 mt-6 italic">{t("alerts_purge_note")}</p>
    </div>
  );
}
